import { useEffect, useReducer, useRef, useState } from 'react';
import PackService from '../../api/packService';
import useAppContext from '../../hooks/useAppContext';
import { PackSettings } from '../../types/packSettings';
import PackSettingsService from '../../api/packSettingsService';
import JobService from '../../api/jobService';
import { Job } from '../../types/job';
import toast from 'react-hot-toast';
import { Level } from '../../types/level';
import { LevelDifficultyPoint } from '../../types/levelDifficultyPoint';
import LevelService from '../../api/levelService';
import React from 'react';
import { normalizeApiLevels, normalizeDifficultyPoints } from './difficultyPoints';

type Action =
  | { type: 'fetch' }
  | {
      type: 'loaded';
      packId: number;
      settings: PackSettings[];
      maps: Level[];
      difficultyPoints: LevelDifficultyPoint[];
    }
  | {
      type: 'difficulty-updated';
      points: LevelDifficultyPoint[];
    }
  | {
      type: 'levels-refreshed';
      maps: Level[];
      difficultyPoints: LevelDifficultyPoint[];
    };

interface MapsState {
  PackId: number;
  Settings: PackSettings[];
  Maps: Level[];
  DifficultyPoints: LevelDifficultyPoint[];
  Loading: boolean;
}

const DefaultPackState = {
  PackId: 0,
  Settings: [],
  Maps: [],
  DifficultyPoints: [],
  Loading: false,
} as MapsState;

const reducer = (state: MapsState, action: Action): MapsState => {
  switch (action.type) {
    case 'fetch':
      return { ...state, Loading: true };
    case 'loaded': {
      const maps = normalizeApiLevels(action.maps);
      const apiPoints = normalizeDifficultyPoints(action.difficultyPoints);
      const normalizedDifficulty = maps
        .slice()
        .sort((a, b) => a.number - b.number)
        .map((level) => ({ number: level.number, difficulty: level.difficulty }));
      return {
        ...state,
        PackId: action.packId,
        Settings: [...action.settings],
        Maps: [...maps],
        DifficultyPoints: apiPoints.length > 0 ? apiPoints : normalizedDifficulty,
        Loading: false,
      };
    }
    case 'difficulty-updated': {
      const points = normalizeDifficultyPoints(action.points);
      return {
        ...state,
        DifficultyPoints: [...points],
        Maps: state.Maps.map((level) => {
          const found = points.find((point) => point.number === level.number);
          if (!found) {
            return level;
          }

          return {
            ...level,
            difficulty: found.difficulty,
          };
        }),
      };
    }
    case 'levels-refreshed': {
      const maps = normalizeApiLevels(action.maps);
      const apiPoints = normalizeDifficultyPoints(action.difficultyPoints);
      const normalizedDifficulty = maps
        .slice()
        .sort((a, b) => a.number - b.number)
        .map((level) => ({ number: level.number, difficulty: level.difficulty }));
      return {
        ...state,
        Maps: [...maps],
        DifficultyPoints: apiPoints.length > 0 ? apiPoints : normalizedDifficulty,
      };
    }
    default:
      return state;
  }
};

export const useMapsReducer = () => {
  const [data, dispatch] = useReducer(reducer, DefaultPackState);
  const [recalculateLoading, setRecalculateLoading] = useState<boolean>(false);
  const [reorderLoading, setReorderLoading] = useState<boolean>(false);
  const { Lang } = useAppContext();
  const loading = useRef<boolean>(false);

  const fetchData = React.useCallback(async () => {
    dispatch({ type: 'fetch' });
    const pack = await PackService.getCurrent(Lang);
    const maps = await LevelService.getLevels(Lang);
    const difficultyPoints = await LevelService.getDifficultyPoints(Lang);
    const settings = await PackSettingsService.getAll();
    dispatch({
      type: 'loaded',
      packId: pack.id,
      maps: maps,
      settings: settings,
      difficultyPoints: difficultyPoints,
    });
  }, [Lang]);

  useEffect(() => {
    if (loading.current) return;
    loading.current = true;
    fetchData();
  }, [fetchData]);

  const submitJob = async (job: Job) => {
    const scheduled = await JobService.submit(job);
    if (scheduled) {
      toast.success('job scheduled');
    } else {
      toast.error('failed to schedule');
    }
  };

  const recalculateDifficulty = async () => {
    if (recalculateLoading) {
      return;
    }

    setRecalculateLoading(true);
    try {
      const points = await LevelService.recalculateDifficulty(Lang);
      dispatch({
        type: 'difficulty-updated',
        points: points,
      });
      toast.success('difficulty updated');
    } catch {
      toast.error('failed to recalculate difficulty');
    } finally {
      setRecalculateLoading(false);
    }
  };

  const reorderLevelsByDifficulty = async () => {
    if (reorderLoading) {
      return;
    }

    setReorderLoading(true);
    try {
      const points = await LevelService.reorderLevelsByDifficulty(Lang);
      const maps = await LevelService.getLevels(Lang);
      dispatch({
        type: 'levels-refreshed',
        maps: maps,
        difficultyPoints: points,
      });
      toast.success('levels reordered');
    } catch {
      toast.error('failed to reorder levels');
    } finally {
      setReorderLoading(false);
    }
  };

  return {
    data,
    submitJob,
    recalculateDifficulty,
    recalculateLoading,
    reorderLevelsByDifficulty,
    reorderLoading,
  };
};
